import { ChannelIcon } from '@src/safe-zones/channelIcons';
import {
  listRegisteredDefinitions,
  SAFE_ZONE_REGISTRY,
} from '@src/safe-zones/registry';
import { SAFE_ZONE_STRINGS } from '@src/safe-zones/strings';
import {
  type SafeZoneChannel,
  splitSafeZoneSelection,
  SafeZoneDefinition,
  SafeZoneSelection,
  SafeZoneVariantKey,
} from '@src/safe-zones/types';
import { SAFE_ZONE_Z_INDEX } from '@src/safe-zones/zIndex';
import { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Tooltip } from 'react-tooltip';
import styled, { css } from 'styled-components';

import { COMPACT_TOOLTIP_STYLE } from '../common/tooltipStyles';
import {
  AspectDropDown,
  AspectItem,
  AspectItemContent,
  AspectRow,
  CaretDownIcon,
  DropdownIcon,
} from '../stage/AspectRatioUiPrimitives';
import { useOutsideAlerter } from '../transcript/useClickOutside';
import SafeZoneVariantToggles from './SafeZoneVariantToggles';

interface Props {
  selection: SafeZoneSelection;
  selectedDimension: string;
  onSelect: (selection: SafeZoneSelection) => void;
  disabled?: boolean;
  disabledTooltip?: string;
  /** Read each variant's current boolean from the session store. */
  isVariantActive: (key: SafeZoneVariantKey) => boolean;
  /** Flip a variant flag (fires analytics at the call site). */
  onToggleVariant: (key: SafeZoneVariantKey) => void;
}

const COPY = SAFE_ZONE_STRINGS.dropdown;

function isAspectRatioSupported(
  def: SafeZoneDefinition,
  dimension: string,
): boolean {
  return def.supportedAspectRatios.some(ar => ar === dimension);
}

function activeChannel(selection: SafeZoneSelection): SafeZoneChannel | null {
  if (selection === 'none') return null;
  if (!SAFE_ZONE_REGISTRY[selection]) return null;
  return splitSafeZoneSelection(selection).channel;
}

const SafeZoneDropdown = ({
  selection,
  selectedDimension,
  onSelect,
  disabled,
  disabledTooltip,
  isVariantActive,
  onToggleVariant,
}: Props) => {
  const [menuRect, setMenuRect] = useState<DOMRect | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const disabledTooltipId = 'safe-zone-disabled-tooltip';

  useOutsideAlerter(dropdownRef, (event: MouseEvent) => {
    if (
      menuRef.current &&
      event.target instanceof Node &&
      menuRef.current.contains(event.target)
    ) {
      return;
    }
    setMenuRect(null);
  });

  const definitions = useMemo(
    () =>
      listRegisteredDefinitions().filter(def =>
        isAspectRatioSupported(def, selectedDimension),
      ),
    [selectedDimension],
  );
  const channel = activeChannel(selection);

  return (
    <Main ref={dropdownRef}>
      <AspectItem
        onClick={e => {
          if (disabled) return;
          const rect = e.currentTarget.getBoundingClientRect();
          setMenuRect(prev => (prev ? null : rect));
        }}
        style={disabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
        data-testid="safe-zone-dropdown-trigger"
        data-tooltip-id={
          disabled && disabledTooltip ? disabledTooltipId : undefined
        }
        data-tooltip-content={
          disabled && disabledTooltip ? disabledTooltip : undefined
        }
      >
        <AspectItemContent>
          <Label>{COPY.label}</Label>
          {channel && (
            <ActiveIcon>
              <ChannelIcon channel={channel} size={16} />
            </ActiveIcon>
          )}
        </AspectItemContent>
        <DropdownIcon isOpen={!!menuRect}>
          <CaretDownIcon />
        </DropdownIcon>
      </AspectItem>

      {disabled && disabledTooltip && (
        <Tooltip
          id={disabledTooltipId}
          style={COMPACT_TOOLTIP_STYLE}
          place="bottom"
          positionStrategy="fixed"
          noArrow
        />
      )}

      {menuRect &&
        createPortal(
          <SafeZoneDropDownPanel
            ref={menuRef}
            style={{ top: menuRect.bottom + 2, left: menuRect.left }}
          >
            {selection !== 'none' && (
              // Tools row: per-overlay variant pills. Lives at the top of the
              // panel (not as a selectable row) — its controls operate
              // independently of the overlay selection below.
              <ToolsRow data-testid="safe-zone-tools-row">
                <SafeZoneVariantToggles
                  selection={selection}
                  isActive={isVariantActive}
                  onToggle={onToggleVariant}
                />
              </ToolsRow>
            )}

            <Row
              isSelected={selection === 'none'}
              isDisabled={false}
              onClick={() => {
                onSelect('none');
                setMenuRect(null);
              }}
              data-testid="safe-zone-row-none"
            >
              <RowLabel>{COPY.none}</RowLabel>
            </Row>

            {definitions.map(def => {
              const isSelected = selection === def.key;
              return (
                <Row
                  key={def.key}
                  isSelected={isSelected}
                  isDisabled={false}
                  onClick={() => {
                    onSelect(def.key);
                    setMenuRect(null);
                  }}
                  data-testid={`safe-zone-row-${def.key}`}
                >
                  <RowLabel>
                    {def.channelLabel} — {def.formatLabel}
                  </RowLabel>
                  <RowChannelIcon>
                    <ChannelIcon channel={def.channel} size={16} />
                  </RowChannelIcon>
                </Row>
              );
            })}
          </SafeZoneDropDownPanel>,
          document.body,
        )}
    </Main>
  );
};

export default SafeZoneDropdown;

const Main = styled.div`
  position: relative;
`;

const ActiveIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  line-height: 0;
`;

/* Title Case, no text-transform — design-system typography rules forbid
   all-caps anywhere. Matches SelectedAspectRatio's scale on the sibling
   aspect-ratio trigger. */
const Label = styled.span`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
  margin-right: 4px;
`;

const SafeZoneDropDownPanel = styled(AspectDropDown)`
  position: fixed;
  /* Wide enough to fit the variant pill cluster (Player Controls + Captions)
     on the tools row without wrapping. The (i) info anchor + "Player Controls
     Off" label + cluster gap + (i) anchor + "Captions Off" label barely fits
     under 280, so we set the panel a touch wider for breathing room. */
  width: 300px;
  /* Above the safe-zone overlay so the panel's rows aren't covered by the
     overlay's right-side action rail / bottom strip. See
     safe-zones/zIndex.ts for the layer rationale. */
  z-index: ${SAFE_ZONE_Z_INDEX.dropdownPanel};
  max-height: 60vh;
  overflow-y: auto;
`;

// The tools row hosts the hint-toggle + variant pills. It's visually distinct
// from the selectable rows below — tighter spacing, separator beneath — so
// users read it as "controls", not "another preview to pick".
const ToolsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-bottom: 1px solid var(--border);
  margin-bottom: 2px;
`;

const disabledRowCss = css<{ isDisabled?: boolean }>`
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ isDisabled }) => (isDisabled ? 0.5 : 1)};
  pointer-events: ${({ isDisabled }) => (isDisabled ? 'none' : 'auto')};
`;

const Row = styled(AspectRow)<{ isDisabled?: boolean }>`
  ${disabledRowCss}
`;

const RowLabel = styled.span`
  white-space: nowrap;
`;

// Right-justified channel badge inside a row — `margin-left: auto` pushes
// it to the row's right edge regardless of label length, so every row's
// icon column stays aligned vertically.
const RowChannelIcon = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: auto;
  flex: 0 0 auto;
`;
